import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { MailWarning, AlertTriangle, RotateCw, LogOut } from 'lucide-react';
import { AuthProvider, useAuth } from './lib/auth';
import { ThemeProvider } from './lib/theme';
import { ClerkBoot } from './lib/clerk';
import { BillingProvider } from './lib/billing';
import AdminDashboard from './components/AdminDashboard';
import Login from './pages/Login';
import Settings from './pages/Settings';
import Webhooks from './pages/Webhooks';
import AuthCallback from './pages/AuthCallback';
import Forms from './pages/Forms';
import Quizzes from './pages/Quizzes';
import Buttons from './pages/Buttons';
import Popups from './pages/Popups';
import AiConnector from './pages/AiConnector';
import AdminHomepage from './pages/AdminHomepage';
import AdminUsers from './pages/AdminUsers';
import VaultDashboard from './pages/VaultDashboard';
import PlatformConnections from './pages/PlatformConnections';
import Backups from './pages/Backups';
import Snapshots from './pages/Snapshots';
import DataBrowser from './pages/DataBrowser';
import Restores from './pages/Restores';
import Schedules from './pages/Schedules';
import AdminSystemSettings from './pages/AdminSettings';
import Teams from './pages/Teams';
import TeamAccept from './pages/TeamAccept';
import ApiKeys from './pages/ApiKeys';
import Billing from './pages/Billing';
import Workspaces from './pages/Workspaces';
import McpAuthorize from './pages/McpAuthorize';

function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-line border-t-accent"></div>
    </div>
  );
}

/** Signed in with Clerk, but the API won't hand over the account (yet). */
function AccountIssue() {
  const { issue, refreshUser, logout } = useAuth();
  const [retrying, setRetrying] = React.useState(false);
  const unverified = issue === 'email_unverified';
  const Icon = unverified ? MailWarning : AlertTriangle;

  const retry = async () => {
    setRetrying(true);
    await refreshUser();
    setRetrying(false);
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6">
      <div className="w-full max-w-[420px] bg-surface border border-line rounded-[18px] shadow-card p-7 text-center animate-fade">
        <div className="mx-auto w-11 h-11 rounded-[12px] bg-warning-soft text-warning flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <h1 className="mt-4 font-display font-bold text-[20px] text-ink tracking-tight">
          {unverified ? 'Verify your email address' : "We couldn't load your account"}
        </h1>
        <p className="mt-2 text-[13.5px] font-medium text-ink-2">
          {unverified
            ? 'Check your inbox for the verification email, confirm your address, then continue.'
            : 'Something went wrong talking to My Lead Syncer. Please try again in a moment.'}
        </p>
        <div className="mt-5 flex justify-center gap-2.5">
          <button onClick={() => void logout()} className="ds-btn ds-btn-ghost">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          <button onClick={() => void retry()} disabled={retrying} className="ds-btn ds-btn-primary disabled:opacity-60">
            <RotateCw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} /> {unverified ? "I've verified" : 'Try again'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isSignedIn } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isSignedIn) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return <AccountIssue />;
  }

  return <>{children}</>;
}

function RootRedirect() {
  const { loading, isSignedIn } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return <Navigate to={isSignedIn ? '/dashboard' : '/login'} replace />;
}

// Module-level so ClerkProvider (which wraps the router) can navigate with it.
const router = createBrowserRouter([
        {
          path: '/',
          element: <RootRedirect />,
        },
        {
          // Clerk path routing: its sub-steps (factor-two, reset password…) live under /login/*.
          path: '/login/*',
          element: <Login mode="sign-in" />,
        },
        {
          path: '/register/*',
          element: <Login mode="sign-up" />,
        },
        // Old email links from the pre-Clerk auth — Clerk now owns these flows.
        { path: '/verify-email', element: <Navigate to="/login" replace /> },
        { path: '/reset-password', element: <Navigate to="/login" replace /> },
        { path: '/forgot-password', element: <Navigate to="/login" replace /> },
        {
          // MCP OAuth consent: handles its own signed-out state (inline <SignIn/>).
          path: '/mcp/authorize',
          element: <McpAuthorize />,
        },
        {
          path: '/auth/callback',
          element: (
            <ProtectedRoute>
              <AuthCallback />
            </ProtectedRoute>
          ),
        },
        {
          path: '/dashboard',
          element: (
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          ),
        },
        {
          path: '/settings',
          element: (
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          ),
        },
        {
          path: '/webhooks',
          element: (
            <ProtectedRoute>
              <Webhooks />
            </ProtectedRoute>
          ),
        },
        {
          path: '/forms',
          element: (
            <ProtectedRoute>
              <Forms />
            </ProtectedRoute>
          ),
        },
        {
          path: '/quizzes',
          element: (
            <ProtectedRoute>
              <Quizzes />
            </ProtectedRoute>
          ),
        },
        {
          path: '/buttons',
          element: (
            <ProtectedRoute>
              <Buttons />
            </ProtectedRoute>
          ),
        },
        {
          path: '/popups',
          element: (
            <ProtectedRoute>
              <Popups />
            </ProtectedRoute>
          ),
        },
        {
          path: '/teams',
          element: (
            <ProtectedRoute>
              <Teams />
            </ProtectedRoute>
          ),
        },
        {
          path: '/teams/accept',
          element: (
            <ProtectedRoute>
              <TeamAccept />
            </ProtectedRoute>
          ),
        },
        {
          path: '/api-keys',
          element: (
            <ProtectedRoute>
              <ApiKeys />
            </ProtectedRoute>
          ),
        },
        {
          path: '/billing',
          element: (
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          ),
        },
        {
          path: '/workspaces',
          element: (
            <ProtectedRoute>
              <Workspaces />
            </ProtectedRoute>
          ),
        },
        {
          path: '/ai-connector',
          element: (
            <ProtectedRoute>
              <AiConnector />
            </ProtectedRoute>
          ),
        },
        {
          path: '/admin/homepage',
          element: (
            <ProtectedRoute>
              <AdminHomepage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/admin/users',
          element: (
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          ),
        },
        {
          path: '/admin/system-settings',
          element: (
            <ProtectedRoute>
              <AdminSystemSettings />
            </ProtectedRoute>
          ),
        },
        {
          path: '/vault',
          element: (
            <ProtectedRoute>
              <VaultDashboard />
            </ProtectedRoute>
          ),
        },
        {
          path: '/vault/connections',
          element: (
            <ProtectedRoute>
              <PlatformConnections />
            </ProtectedRoute>
          ),
        },
        {
          path: '/vault/backups',
          element: (
            <ProtectedRoute>
              <Backups />
            </ProtectedRoute>
          ),
        },
        {
          path: '/vault/snapshots',
          element: (
            <ProtectedRoute>
              <Snapshots />
            </ProtectedRoute>
          ),
        },
        {
          path: '/vault/snapshots/:id/browse',
          element: (
            <ProtectedRoute>
              <DataBrowser />
            </ProtectedRoute>
          ),
        },
        {
          path: '/vault/restores',
          element: (
            <ProtectedRoute>
              <Restores />
            </ProtectedRoute>
          ),
        },
        {
          path: '/vault/schedules',
          element: (
            <ProtectedRoute>
              <Schedules />
            </ProtectedRoute>
          ),
        },
      ]);

export default function App() {
  return (
    <ThemeProvider>
      <ClerkBoot
        routerPush={(to) => void router.navigate(to)}
        routerReplace={(to) => void router.navigate(to, { replace: true })}
      >
        <AuthProvider>
          <BillingProvider>
            <RouterProvider router={router} />
          </BillingProvider>
        </AuthProvider>
      </ClerkBoot>
    </ThemeProvider>
  );
}
